import { useEffect, useState } from 'react';
import { Header } from '@/components/Header';
import { BalanceCards } from '@/components/BalanceCards';
import { ShieldCard, KatCard } from '@/components/RewardCards';
import { CiferGallery } from '@/components/CiferGallery';
import { SocialEngagement } from '@/components/SocialEngagement';
import { RewardsHistory } from '@/components/RewardsHistory';
import { ConnectPrompt } from '@/components/ConnectPrompt';
import { AdminPanel } from '@/components/AdminPanel';
import { LegacyStaking } from '@/components/LegacyStaking';
import { useWallet } from '@/hooks/useWallet';
import { useBalances } from '@/hooks/useBalances';
import { useShieldApi } from '@/hooks/useShieldApi';
import { useCiferOnChain } from '@/hooks/useCiferOnChain';


export default function App() {
  const wallet = useWallet();
  const { balances, loading: balancesLoading } = useBalances(wallet.address);
  const api = useShieldApi(wallet.address, wallet.provider);
  const ciferOnChain = useCiferOnChain(wallet.address);

  // Check for special modes in URL hash
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isLegacyMode, setIsLegacyMode] = useState(false);

  // Handle Twitter OAuth callback params in URL
  const [twitterNotice, setTwitterNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Hash-based routing
  useEffect(() => {
    const checkHash = () => {
      setIsAdminMode(window.location.hash === '#admin');
      setIsLegacyMode(window.location.hash === '#legacy');
    };
    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  // Handle Twitter OAuth callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linked = params.get('twitter_linked');
    const error = params.get('twitter_error');
    const handle = params.get('handle');

    if (linked) {
      setTwitterNotice({ type: 'success', message: `@${linked} linked successfully!` });
      window.history.replaceState({}, '', window.location.pathname);
      api.refresh();
    } else if (error) {
      const detail = params.get('detail');
      const errorMessages: Record<string, string> = {
        missing_params: 'OAuth callback missing parameters.',
        invalid_state: 'Invalid OAuth state. Please try again.',
        expired: 'OAuth session expired. Please try again.',
        token_exchange_failed: 'Failed to authenticate with Twitter.',
        user_fetch_failed: 'Failed to fetch Twitter profile.',
        already_linked: handle ? `@${handle} is already linked to another wallet.` : 'This Twitter account is already linked.',
        internal: 'Something went wrong. Please try again.',
        access_denied: 'Twitter authorization was denied.',
      };
      const msg = error ? errorMessages[error] || `Twitter error: ${error}` : 'Unknown error';
      setTwitterNotice({ type: 'error', message: detail ? `${msg} (${detail})` : msg });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Use API data when available, fallback to defaults
  const totalShield = api.shield?.totalShield ?? api.shield?.shieldScore ?? 0;
  const activeShield = api.shield?.activeShield ?? api.shield?.shieldScore ?? 0;
  const cifersRedeemed = api.shield?.cifersRedeemed ?? 0;
  const shieldDailyRate = api.shield?.dailyRate ?? 0;
  const shieldBreakdown = api.shield?.breakdown ?? { stablecoin: 0, caps: 0, social: 0, hold: 0 };
  const katTotal = api.kat?.totalKatEarned ?? 0;
  const katDailyRate = api.kat?.latestDailyAmount ?? 0;

  // Social: use API data if available
  const socialConnected = !!api.social?.twitter;
  const socialHandle = api.social?.twitter?.handle ? `@${api.social.twitter.handle}` : '';
  const socialLinkedSince = api.social?.twitter?.linkedAt
    ? new Date(api.social.twitter.linkedAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const socialTodayPoints = api.social?.todayPoints ?? 0;

  // History: use API data
  const historyEntries = api.history?.length ? api.history : [];

  // Show admin panel if hash is #admin
  if (isAdminMode) {
    return <AdminPanel provider={wallet.provider} />;
  }

  // Show legacy staking if hash is #legacy
  if (isLegacyMode && wallet.address) {
    return (
      <div className="relative z-[1]">
        <Header wallet={wallet} />
        <main className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-[80px] sm:pt-[100px] pb-12 sm:pb-20">
          <LegacyStaking wallet={wallet} capsBalance={balances.caps} />
        </main>
      </div>
    );
  }

  return (
    <div className="relative z-[1]">
      <Header wallet={wallet} />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-[80px] sm:pt-[100px] pb-12 sm:pb-20">
        {/* Twitter OAuth notice banner */}
        {twitterNotice && (
          <div
            className={`mb-6 p-4 rounded-ternoa border text-sm font-medium animate-fade-in-up flex items-center justify-between ${
              twitterNotice.type === 'success'
                ? 'bg-success/10 border-success/30 text-success'
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}
          >
            <span>{twitterNotice.message}</span>
            <button
              onClick={() => setTwitterNotice(null)}
              className="ml-4 text-current opacity-60 hover:opacity-100 bg-transparent border-none cursor-pointer text-lg"
            >
              ×
            </button>
          </div>
        )}

        {!wallet.address ? (
          <ConnectPrompt
            onConnect={wallet.connect}
            isConnecting={wallet.isConnecting}
            error={wallet.error}
          />
        ) : (
          <>
            {/* Page header */}
            <div className="mb-8 sm:mb-12 animate-fade-in-up">
              <div className="inline-block font-display text-[11px] font-bold uppercase tracking-[0.12em] px-5 py-2 bg-accent-light border border-hairline text-accent rounded-ternoa mb-4 sm:mb-5">
                Dashboard
              </div>
              <h1 className="font-display text-2xl sm:text-5xl font-semibold tracking-tight leading-tight">
                Ternoa Shield
              </h1>
              <p className="text-sm sm:text-lg text-gray-400 mt-2 sm:mt-3">
                Deposit your USDC and USDT on Ternoa to secure our ecosystem. Get between 6% and 10% APR, distributed every week in $CAPS.
              </p>
            </div>

            {/* Balances (4-col row) */}
            <div id="balances" className="mb-8 sm:mb-12 animate-fade-in-up scroll-mt-24" style={{ animationDelay: '0.1s' }}>
              <BalanceCards balances={{ ...balances, kat: katTotal }} loading={balancesLoading} />
            </div>

            {/* Shield + KAT (2-col row) */}
            <div id="rewards" className="mb-8 sm:mb-12 animate-fade-in-up scroll-mt-24" style={{ animationDelay: '0.2s' }}>
              <h2 className="section-label">Rewards & Distribution</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                <ShieldCard
                  totalShield={totalShield}
                  activeShield={activeShield}
                  cifersRedeemed={cifersRedeemed}
                  dailyRate={shieldDailyRate}
                  breakdown={shieldBreakdown}
                  onRedeem={api.redeemCifer}
                />
                <KatCard total={katTotal} dailyRate={katDailyRate} />
              </div>
            </div>

            {/* CIFERs Gallery */}
            <div id="cifers" className="mb-8 sm:mb-12 animate-fade-in-up scroll-mt-24" style={{ animationDelay: '0.25s' }}>
              <CiferGallery
                onChain={ciferOnChain.cifers}
                redeemed={api.cifers}
                loading={ciferOnChain.loading}
              />
            </div>

            {/* Social */}
            <div id="social" className="mb-8 sm:mb-12 animate-fade-in-up scroll-mt-24" style={{ animationDelay: '0.3s' }}>
              <SocialEngagement
                connected={socialConnected}
                handle={socialHandle}
                linkedSince={socialLinkedSince}
                todayPoints={socialTodayPoints}
                missions={api.missions}
                onStartAuth={api.startTwitterAuth}
                onUnlinkTwitter={api.unlinkTwitter}
                onVerifyMissionAction={api.verifyMissionAction}
              />
            </div>

            {/* History */}
            <div id="activity" className="mb-8 sm:mb-12 animate-fade-in-up scroll-mt-24" style={{ animationDelay: '0.4s' }}>
              <RewardsHistory entries={historyEntries} />
            </div>

            {/* Footer */}
            <div className="text-center py-8 sm:py-16 text-muted text-xs sm:text-sm animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <div className="mb-2">
                <span className="text-accent font-display font-semibold">ternoa<span className="text-accent">.</span></span>
                <span className="mx-2 opacity-30">|</span>
                The Web3 Security Toolkit
              </div>
              <div>Shield & KAT rewards update daily at 00:00 UTC. Balances are indicative.</div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
